// The notch's panel: what a click on its grip opens, growing out of the notch at the grip's edge, a rail of tabs beside
// their content. A tab is its owner's, added with addTab (@webtop/api's `notch.addTab`): Audio is the Audio applet's
// (applets/audio), Assistant the Assistant applet's, User the user applet's, Settings the Settings applet's. It is no overlay: the webtop stays in view and in use
// behind it, and a press anywhere else (but in a menu or dialog over it) or Escape closes it; one overlay or the panel shows at a time
// (openTab here, openOverlay there). Where it sits is client/lib/notch.js's.
// It also keeps what applets register with the notch: `mic` and `speaker`, the Audio applet's, which other applets reach as
// `notch.mic` and `notch.speaker` whether or not that applet is up yet (registerPart, the parts below).
import { lineIcon } from '../widgets/kit.js';
import { appletService, AUDIO } from './applets.js';
import { clearAddress, closeOverlay } from './overlay.js';
import { toast } from './ui.js';

const $notch = document.getElementById('notch');
const $panel = document.getElementById('notch-panel');
const $rail = document.getElementById('notch-rail');
const $tab = document.getElementById('notch-tab');
const $grip = document.getElementById('notch-grip');
const $menu = document.getElementById('menu');

const ICON_NAME = /^[a-z0-9-]{1,40}$/; // a file in client/icons; anything else is an address
const tabs = new Map(); // id -> {id, label, icon, order, end, when?, render}
let shown = null; // the id of the tab drawn
let chosen = null; // the id last picked, for the next time it opens
let cleanup = null;

const panelOpen = () => !$panel.hidden;
/** The id of the tab showing, or null with the panel closed. */
export const shownTab = () => shown;
const offered = () => [...tabs.values()].filter((t) => !t.when || t.when()).sort((a, b) => a.order - b.order);

/** A tab: `render(el)` draws it and may answer a cleanup, run when it is left or the panel closes. */
export function addTab({ id, label, icon = 'settings', order = 100, end = false, when = null, render = null } = {}) {
  if (!id || !label || typeof render !== 'function') throw new Error('A tab needs an id, a label and render');
  const tab = { id: String(id), label: String(label).slice(0, 24), icon: String(icon).slice(0, 2000), order: Number(order) || 0, end: !!end, when, render };
  if (tabs.has(tab.id)) throw new Error(`There is already a tab "${tab.id}"`);
  tabs.set(tab.id, tab);
  if (panelOpen()) draw();
  return () => {
    if (tabs.get(tab.id) !== tab) return;
    tabs.delete(tab.id);
    if (panelOpen()) draw();
  };
}

/** Opens the panel on tab `id`, in place of any overlay; false when no such tab is offered now. */
export function openTab(id) {
  const tab = offered().find((t) => t.id === id);
  if (!tab) return false;
  closeOverlay();
  clearAddress(); // `#settings` brought it here, and a reload would open it again
  chosen = tab.id;
  if (panelOpen()) draw();
  else openPanel();
  return true;
}

function leaveTab() {
  try { cleanup?.(); } catch (e) { console.error(e); }
  cleanup = null;
  shown = null;
  $tab.replaceChildren();
}
function drawRail(list) {
  $rail.replaceChildren(...list.map((t) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `notch-rail-btn${t.id === shown ? ' on' : ''}${t.end ? ' end' : ''}`;
    b.title = t.label;
    b.setAttribute('aria-label', t.label);
    b.innerHTML = ICON_NAME.test(t.icon) ? lineIcon(t.icon) : lineIcon('settings');
    // An applet's own mark, by address: a mask like the rest, so it takes the rail's colours.
    if (!ICON_NAME.test(t.icon)) b.firstChild.style.setProperty('--icon', `url("${t.icon.replace(/["\\\n]/g, '')}")`);
    b.addEventListener('click', () => pick(t));
    return b;
  }));
}
function draw() {
  const list = offered();
  const next = list.find((t) => t.id === chosen) ?? list[0];
  if (next?.id !== shown || (shown && !tabs.has(shown))) {
    leaveTab();
    if (next) {
      shown = next.id;
      try { cleanup = next.render($tab) ?? null; } catch (e) { console.error(e); toast(e); }
    }
  }
  drawRail(list);
}
function pick(t) {
  chosen = t.id;
  draw();
}

export function openPanel({ focus = false } = {}) {
  if (panelOpen()) return;
  $panel.hidden = false;
  $notch.classList.add('open');
  $grip.setAttribute('aria-expanded', 'true');
  draw();
  if (focus) $rail.querySelector('.on')?.focus();
}
export function closePanel() {
  if (!panelOpen()) return;
  leaveTab();
  $panel.hidden = true;
  $notch.classList.remove('open');
  $grip.setAttribute('aria-expanded', 'false');
}
export const togglePanel = (o) => (panelOpen() ? closePanel() : openPanel(o));

// A registered part of the notch as callers see it: what it reads, its idle value until the part is registered; what it
// does, which needs the part; and on / watch, kept across its coming and going, so an applet may listen from its own start
// and through a restart of the Audio applet.
function part(idle, calls, waits = {}) {
  let impl = null;
  const listeners = new Set();
  const watchers = new Set(); // {set, stop}
  const hook = (w) => { w.stop?.(); w.stop = impl?.watch(w.set) ?? null; if (!impl) w.set(0); };
  const told = () => { for (const fn of [...listeners]) { try { fn(); } catch (e) { console.error(e); } } };
  const must = () => {
    if (!impl) throw new Error('The Audio applet is not running.');
    return impl;
  };
  const out = {
    provide(next) {
      const off = next.on(told);
      impl = next;
      for (const w of watchers) hook(w);
      told();
      return () => {
        off();
        if (impl !== next) return;
        impl = null;
        for (const w of watchers) hook(w);
        told();
      };
    },
    on(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    watch(set) {
      const w = { set, stop: null };
      watchers.add(w);
      hook(w);
      return () => { watchers.delete(w); w.stop?.(); };
    },
  };
  for (const [name, value] of Object.entries(idle)) Object.defineProperty(out, name, { enumerable: true, get: () => (impl ? impl[name] : value) });
  for (const name of calls) out[name] = (...args) => must()[name](...args);
  for (const [name, when] of Object.entries(waits)) out[name] = (...args) => when(impl, ...args);
  return out;
}
export const mic = part({ available: false, muted: false, device: '', inUse: false, users: [] }, ['mute', 'setDevice'], {
  // The one call that may wait: a press can come before a required applet is up.
  acquire: async (impl, ...args) => {
    if (impl) return impl.acquire(...args);
    await appletService(AUDIO);
    return mic.acquire(...args);
  },
});
export const speaker = part(
  { element: null, canPick: false, volume: 1, muted: false, device: '', silent: false, inUse: false, users: [] },
  ['context', 'play', 'setVolume', 'mute', 'setDevice', 'setSilent'],
  { sound: (impl, ...args) => impl?.sound(...args) ?? null }, // a sound nobody can play yet is just not heard
);
const PARTS = { mic, speaker };
/** `impl` is what the part reads and does, with on(fn) and watch(set); answers its dispose. */
export function registerPart(name, impl) {
  if (!PARTS[name]) throw new Error(`The notch has no part "${name}"`);
  return PARTS[name].provide(impl);
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  addEventListener('pointerdown', (e) => {
    if (panelOpen() && !$notch.contains(e.target) && !$menu.contains(e.target) && !e.target.closest?.('dialog')) closePanel();
  }, true);
  // With a menu or a dialog open over it, Escape closes just that, as for an overlay.
  addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !panelOpen() || !$menu.hidden || document.querySelector('dialog[open]')) return;
    closePanel();
    $grip.focus();
  }, true);
}
